import { useEffect, useState, type FormEvent } from 'react';
import { fetchCbaSettings, updateCbaSettings, type CbaSettingsView } from '../../api/admin';
import { ca } from '../../i18n/ca';
import { errorText } from './shared';
import { fmtMoment } from '../dates';

interface Props {
  onError: (text: string | null) => void;
  onNotice: (text: string | null) => void;
}

/**
 * The platform's own `cba_settings.sqf` (the owner, 2026-09-14): the whole
 * file every generated mission is published with. Pasted here, saved once,
 * and rewritten into the folder of every open session, so a change reaches
 * every mission a server may still load. Before this a generated mission
 * shipped only the Simplex lines and played on the server's own defaults —
 * night vision at ACE's 1 instead of the group's 0.1 (session 32jw3).
 */
export function CbaSettingsTab({ onError, onNotice }: Props) {
  const t = ca.adminUi.cba;
  const [view, setView] = useState<CbaSettingsView | null>(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCbaSettings()
      .then((v) => {
        setView(v);
        setText(v.text);
      })
      .catch((e) => onError(errorText(e)));
  }, [onError]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    onError(null);
    onNotice(null);
    try {
      const saved = await updateCbaSettings(text);
      setView(saved);
      setText(saved.text);
      onNotice(saved.lines === 0 ? t.cleared : t.saved(saved.republished ?? 0));
    } catch (e) {
      onError(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  const when = view?.updatedAt ? fmtMoment(view.updatedAt) : null;

  return (
    <form className="card form" aria-label={t.title} onSubmit={save}>
      <h2>{t.title}</h2>
      <p className="muted small">{t.hint}</p>
      <p className="small">
        {view && view.lines > 0 ? t.lines(view.lines) : t.none}
        {when && <span className="muted"> · {t.savedAt(when)}</span>}
      </p>
      <label>
        {t.label}
        <textarea
          name="cbaSettings"
          className="mono"
          rows={24}
          spellCheck={false}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <div className="row">
        <button
          type="submit"
          className="primary"
          disabled={saving || (view !== null && text === view.text)}
        >
          {saving ? t.saving : t.save}
        </button>
      </div>
    </form>
  );
}
